import { BREAKPOINTS } from '@/config/constants';
import { imageUrlToBase64 } from '@/utils/images';
import Image from 'next/image';
import * as React from 'react';
import { ImageWithFocalPointFieldsFragment } from 'types/generated';

type Sizes = {
  breakpoints?: {
    sm?: string;
    md?: string;
    lg?: string;
    xl?: string;
    xxl?: string;
  };
  default?: string;
};

const generateImageSizesString = (sizes: Sizes): string => {
  // Iterate through the Sizes object and construct the sizes string
  const sizesArray = Object.entries(sizes.breakpoints ?? {}).map(([key, value]) => {
    // Get the breakpoint for the current size
    const breakpoint = BREAKPOINTS[key as keyof typeof BREAKPOINTS];

    // Return the media condition string
    return `(min-width: ${breakpoint}px) ${value}`;
  });

  const defaultSize = sizes.default ? sizes.default : '1224px';

  // Join the array elements into a single string
  return sizesArray.length > 0 ? `${sizesArray.join(', ')},${defaultSize}` : defaultSize;
};

type Props = {
  image: ImageWithFocalPointFieldsFragment;
  sectionId?: string | null;
  className?: string;
  style?: React.CSSProperties;
  contain?: boolean;
  priority?: boolean;
  width?: number;
  height?: number;
  sizes?: Sizes;
};

export const ImageWithFocalPoint = ({
  image,
  sectionId,
  className,
  style,
  contain,
  priority,
  width,
  height,
  sizes,
}: Props): React.ReactElement | null => {
  const [blurDataURL, setBlurDataURL] = React.useState<string | null>(null);
  const [imageLoaded, setImageLoaded] = React.useState<boolean>(false);

  const isSvg = image?.image?.contentType?.includes('svg');
  const hasWidthAndHeight = width && height;

  React.useEffect(() => {
    if (image.image?.url) {
      try {
        imageUrlToBase64(`${image.image.url}?w=10&q=10`).then((dataUrl) => {
          setBlurDataURL(dataUrl);
        });
      } catch (_error) {
        // Fail silently
      }
    }
  }, [image.image?.url]);

  if (!(image.image && image.image.width && image.image.height && image.image.url)) {
    return null;
  }

  const focalPointRelativeX = (image.focalPoint.focalPoint.x / image.image.width) * 100;
  const focalPointRelativeY = (image.focalPoint.focalPoint.y / image.image.height) * 100;

  const aspectRatioProps = hasWidthAndHeight
    ? { width, height }
    : { fill: true, sizes: sizes ? generateImageSizesString(sizes) : '1224px' };

  return (
    <Image
      src={image.image.url}
      alt={image.image.description || `${sectionId}-${image.title}-img`}
      style={{
        objectPosition: `${focalPointRelativeX}% ${focalPointRelativeY}%`,
        ...style,
        ...(hasWidthAndHeight && { width: '100%', height: '100%', aspectRatio: width / height }),
      }}
      className={`${isSvg || contain ? 'object-contain' : 'object-cover'} ${className}`}
      priority={priority}
      {...(blurDataURL && !imageLoaded && { placeholder: 'blur', blurDataURL })}
      onLoad={() => setImageLoaded(true)}
      {...aspectRatioProps}
    />
  );
};
